import { useEffect, useRef, useState } from "react";

export default function CreditLinesSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="credit-lines"
      ref={sectionRef}
      className="py-16 md:py-24 bg-white"
    >
      <div className="px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          <div
            className={`transition-all duration-700 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold tracking-wider uppercase mb-4">
              Next Step
            </span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-dark-950 mb-6">
              After Your Credit Is Restored
            </h2>
            <p className="text-dark-400 text-base md:text-lg leading-relaxed mb-6">
              We do not stop at cleaning up your credit. Once your score is where it needs to be, we can assist you with personal and business credit lines to keep your financial momentum going.
            </p>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-4 p-4 rounded-xl bg-dark-50">
                <div className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center flex-shrink-0">
                  <i className="ri-bank-card-line text-white text-lg" />
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-dark-950 text-sm mb-1">
                    Personal Credit Lines
                  </h4>
                  <p className="text-dark-400 text-xs leading-relaxed">
                    Credit cards, personal loans, auto financing, and mortgage pre-qualification assistance.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-4 rounded-xl bg-dark-50">
                <div className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center flex-shrink-0">
                  <i className="ri-building-2-line text-white text-lg" />
                </div>
                <div>
                  <h4 className="font-heading font-semibold text-dark-950 text-sm mb-1">
                    Business Credit Lines
                  </h4>
                  <p className="text-dark-400 text-xs leading-relaxed">
                    Business credit cards, lines of credit, and vendor accounts to help your company grow.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`relative rounded-xl overflow-hidden transition-all duration-700 delay-200 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="w-full h-72 md:h-96 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=professional%20business%20meeting%20handshake%20over%20a%20desk%20with%20credit%20approval%20documents%20and%20a%20laptop%20showing%20financial%20growth%20charts%2C%20warm%20corporate%20office%20lighting%2C%20trustworthy%20business%20atmosphere%2C%20high%20quality%20photography&width=600&height=500&seq=credit-line-img-001&orientation=landscape"
                alt="Credit lines and business financing assistance after credit restoration"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-dark-950/40 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}